import { FORMAT_LIST_TESTS } from '../../constants';
import CustomSurvey from '../CreateAppointment/ServiceItems/CustomSurvey';
import ServicesItem from './SelectServices/ServicesItem';
import Invoice from '@/module/all-screenings/component/Invoice';
import {
  generateLstTests,
  formatMeasurementInSideListTest,
} from '@/module/all-screenings/utils';
import CustomTabs from '@/new-components/CustomTabs';
import { makeStyles, Paper, Box } from '@material-ui/core';
import { isEmpty } from 'lodash';
import moment from 'moment';
import React, { useState, useEffect, useRef } from 'react';

const useStyles = makeStyles({
  container: {
    display: 'flex',
  },

  leftContainer: {
    overflowY: 'hidden',
    flex: '1',
    borderRight: '1px solid #F1F1F1',
    height: 'calc(100vh - 282px)',
  },

  rightContainer: {
    overflow: 'scroll',
    padding: '16px 40px',
    display: 'flex',
    height: 'calc(100vh - 282px)',
  },
  title: {
    display: 'flex',
    alignItems: 'center',
    paddingLeft: 16,
    fontSize: 20,
    gap: 8,
  },
  disabledSwitch: {
    pointerEvents: 'none',
    cursor: 'not-allowed',
  },
});

const SelecetServiceStep = ({
  apptData,
  projectInfo,
  formValue,
  setFormValue,
  handleChangeData,
  packageDetail,
  setPackageDetail,
  restrictedPackageIds,
  restrictedTestIds,
  projectCustomFields,
  lstOptionalTests,
  setCurrentPackage,
  currentPackage,
  dataPackageExisted,
  locationId,
  dataInfo,
  originalPrices,
  sessionBookedPrices,
  isFollowUp,
}) => {
  /**
   * The date the appointment is being moved to, handed to the BE so it can
   * filter out services unavailable then. Note this is `startDate`, not
   * `formValue.date` - the latter exists but is never updated by the picker.
   * Held either as a moment object (initial load) or a UTC instant (after a
   * picker change), so normalise both the way the Create flow does.
   */
  const bookingDate = formValue?.startDate
    ? moment(formValue.startDate).format('YYYY-MM-DD')
    : '';
  const classes = useStyles();
  const [currentTab, setCurrentTab] = useState(0);
  const [errors, setErrors] = useState({});

  // Get hasNavigatedBetweenSteps from localStorage
  // This will be reset when switching tabs to ensure unselected items show old prices
  const getHasNavigatedBetweenSteps = () => {
    if (apptData?.id) {
      try {
        const stored = localStorage.getItem(
          `hasNavigatedBetweenSteps_${apptData.id}`
        );
        return stored === 'true';
      } catch (e) {
        return false;
      }
    }
    return false;
  };

  // Reset hasNavigatedBetweenSteps in localStorage when switching tabs
  // This ensures unselected items show old prices when switching tabs
  const prevTabRef = useRef(currentTab);
  useEffect(() => {
    if (prevTabRef.current !== currentTab) {
      // Tab changed, reset localStorage to show old prices
      if (apptData?.id) {
        try {
          localStorage.removeItem(`hasNavigatedBetweenSteps_${apptData.id}`);
        } catch (e) {
          console.error(
            'Failed to remove hasNavigatedBetweenSteps from localStorage',
            e
          );
        }
      }
      prevTabRef.current = currentTab;
    }
  }, [apptData.id, currentTab]);

  // Helper to check if projectCustomFields.items is empty
  const hasCustomFieldsItems = () => {
    if (!projectCustomFields || !Array.isArray(projectCustomFields.items))
      return false;
    return projectCustomFields.items.length > 0;
  };

  const TABS = [
    {
      label: 'Packages',
      chipLabel: formValue?.lstPackages?.length,
      body: (
        <ServicesItem
          isPackage
          projectId={projectInfo.id}
          locationId={locationId}
          dataExist={formValue.lstPackages}
          handleChangeData={handleChangeData}
          bookingDate={bookingDate}
          filterKey="lstPackages"
          formValue={formValue}
          setFormValue={setFormValue}
          packageDetail={packageDetail}
          setPackageDetail={setPackageDetail}
          restrictedPackageIds={restrictedPackageIds}
          restrictedTestIds={restrictedTestIds}
          setCurrentPackage={setCurrentPackage}
          currentPackage={currentPackage}
          dataPackageExisted={dataPackageExisted}
          sessionBookedPrices={sessionBookedPrices}
          originalPrices={originalPrices}
          apptData={apptData}
          hasNavigatedBetweenSteps={getHasNavigatedBetweenSteps()}
        />
      ),
    },
    {
      label: 'Profile',
      chipLabel: formValue?.lstProfiles?.length,
      body: (
        <ServicesItem
          isProfile
          projectId={projectInfo.id}
          dataExist={formValue.lstProfiles}
          handleChangeData={handleChangeData}
          bookingDate={bookingDate}
          filterKey="lstProfiles"
          sessionBookedPrices={sessionBookedPrices}
          originalPrices={originalPrices}
          apptData={apptData}
          hasNavigatedBetweenSteps={getHasNavigatedBetweenSteps()}
        />
      ),
    },
    {
      label: 'Measurements',
      chipLabel: formValue?.lstMeasurements?.length,
      body: (
        <ServicesItem
          category="Measurements"
          projectId={projectInfo.id}
          dataExist={formValue.lstMeasurements}
          handleChangeData={handleChangeData}
          bookingDate={bookingDate}
          filterKey="lstMeasurements"
          sessionBookedPrices={sessionBookedPrices}
          originalPrices={originalPrices}
          apptData={apptData}
          hasNavigatedBetweenSteps={getHasNavigatedBetweenSteps()}
        />
      ),
    },
    {
      label: 'Procedure',
      chipLabel: formValue?.lstProcedures?.length,
      body: (
        <ServicesItem
          category="Procedures"
          projectId={projectInfo.id}
          locationId={locationId}
          dataExist={formValue.lstProcedures}
          handleChangeData={handleChangeData}
          bookingDate={bookingDate}
          filterKey="lstProcedures"
          sessionBookedPrices={sessionBookedPrices}
          originalPrices={originalPrices}
          apptData={apptData}
          hasNavigatedBetweenSteps={getHasNavigatedBetweenSteps()}
        />
      ),
    },
    {
      label: 'Radiology',
      chipLabel: formValue?.lstRadiologies?.length,
      body: (
        <ServicesItem
          category="Radiologies"
          projectId={projectInfo.id}
          locationId={locationId}
          dataExist={formValue.lstRadiologies}
          handleChangeData={handleChangeData}
          bookingDate={bookingDate}
          filterKey="lstRadiologies"
          sessionBookedPrices={sessionBookedPrices}
          originalPrices={originalPrices}
          apptData={apptData}
          hasNavigatedBetweenSteps={getHasNavigatedBetweenSteps()}
        />
      ),
    },
    {
      label: 'Vaccination',
      chipLabel: formValue?.lstVaccinations?.length,
      body: (
        <ServicesItem
          category="Vaccinations"
          projectId={projectInfo.id}
          dataExist={formValue.lstVaccinations}
          handleChangeData={handleChangeData}
          bookingDate={bookingDate}
          filterKey="lstVaccinations"
          sessionBookedPrices={sessionBookedPrices}
          originalPrices={originalPrices}
          apptData={apptData}
          hasNavigatedBetweenSteps={getHasNavigatedBetweenSteps()}
        />
      ),
    },
    {
      label: 'Review',
      chipLabel: formValue?.lstReviews?.length,
      body: (
        <ServicesItem
          category="Reviews"
          projectId={projectInfo.id}
          dataExist={formValue.lstReviews}
          handleChangeData={handleChangeData}
          bookingDate={bookingDate}
          filterKey="lstReviews"
          sessionBookedPrices={sessionBookedPrices}
          originalPrices={originalPrices}
          apptData={apptData}
          hasNavigatedBetweenSteps={getHasNavigatedBetweenSteps()}
        />
      ),
    },
    {
      label: 'Custom survey',
      chipCustomSurvey: formValue.lstScreeningFields.length,
      chipRequired: hasCustomFieldsItems(),
      body: (
        <CustomSurvey
          errors={errors}
          setErrors={setErrors}
          formValue={formValue}
          setFormValue={setFormValue}
          dataDefault={projectCustomFields}
          dataExist={formValue.lstScreeningFields}
        />
      ),
    },
  ];

  return (
    <Paper square elevation={0} className={classes.container}>
      <Box
        className={classes.leftContainer}
        flexDirection="column"
        gridGap={16}
      >
        <Box display="flex" overflowX="hidden">
          <CustomTabs
            listTab={TABS}
            value={currentTab}
            onChange={(e, val) => {
              setCurrentTab(val);
            }}
            renderLabel="label"
            renderKey="label"
            orientation="vertical"
            height="calc(100vh - 282px)"
          />
          {TABS[currentTab].body}
        </Box>
      </Box>

      <Box className={classes.rightContainer}>
        <Invoice
          lstTests={formatMeasurementInSideListTest(
            generateLstTests(formValue)
          )}
          apptData={apptData}
          lstProfiles={formValue?.lstProfiles}
          lstPackages={formValue?.lstPackages}
          ignoredProfiles={formValue?.ignoredProfiles}
          ignoredTests={formValue?.ignoredTests}
          lstOptionalTests={lstOptionalTests}
          staffType={formValue?.staffType || dataInfo?.staffType}
          projectInfo={projectInfo}
          originalPrices={originalPrices}
          isFollowUp={isFollowUp}
        />
      </Box>
    </Paper>
  );
};

export default SelecetServiceStep;
